import { ApiError, type SignInResult, type User } from "@rhema/client";
import { Button, ChevronLeftIcon, cn, focusRing, Logo, MailIcon, TextField, UiLink } from "@rhema/ui";
import { type FormEvent, type ReactNode, useState } from "react";
import { useSearchParams } from "react-router";
import { usePhoneEnabled } from "../session/features";
import { returnPath } from "../session/gates";
import { useSession } from "../session/session";
import { NameStep, PhoneCodeStep, PhoneStartForm, PinStep } from "./PhoneSignIn";
import { OrDivider, optionClass, ProviderButtons } from "./ProviderButtons";

export type SignInProvider = "google" | "apple" | "microsoft";
export type SignInStage = "start" | "mfa" | "verify";
/** Signing in to an account, or making one. Phone sign-in does both, so they differ in words. */
export type AuthMode = "signin" | "signup";

export type SignInScreenProps = {
  onProvider: (provider: SignInProvider) => void;
  onEmail: (email: string, password: string) => void;
  /** Starts phone sign-in with an E.164 number. Without it the phone form is left out. */
  onPhone?: (phone: string) => void;
  onMfa?: (code: string) => void;
  onBack?: () => void;
  stage?: SignInStage;
  mode?: AuthMode;
  /** Shown above the buttons when sign in fails. */
  error?: string | null;
  /** Disables the buttons while a sign in is in progress. */
  busy?: boolean;
};

const SITE = "https://rhema.siliconblast.com";
const BARS = [10, 18, 12, 22, 14, 8, 16];

/** The blue panel beside the form on wide screens. Phones show only the logo row instead. */
function BrandPanel() {
  return (
    <section
      aria-label="About Rhema"
      className="relative hidden w-[min(44%,620px)] flex-none flex-col justify-between overflow-hidden rounded-[28px] bg-blue px-11 py-10 text-linen min-[900px]:flex"
    >
      <div className="flex items-center gap-3">
        <Logo size={40} variant="tile" />
        <span className="font-serif text-[28px] font-medium">rhema</span>
      </div>
      <div aria-hidden="true" className="flex flex-col gap-3.5 px-2">
        <div className="max-w-[300px] self-start rounded-[22px_22px_22px_8px] bg-linen px-[18px] py-3.5 text-[17px] text-ink">
          Are we still on for Saturday?
        </div>
        <div className="self-end rounded-[22px_22px_8px_22px] bg-blue-container px-[18px] py-3.5 text-[17px] text-ink">
          Yes. Ten at the market.
        </div>
        <div className="flex items-center gap-3 self-start rounded-full bg-white/15 px-[18px] py-3 text-[15px]">
          <span className="flex items-center gap-[3px]">
            {BARS.map((h, i) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional
                key={i}
                className={cn("w-[3px] rounded-[3px]", i < 3 ? "bg-linen" : "bg-linen/50")}
                style={{ height: h }}
              />
            ))}
          </span>
          Voice message, 0:18
        </div>
      </div>
      <div>
        <p className="m-0 max-w-[470px] font-serif text-[clamp(34px,3.2vw,46px)] leading-[1.08] tracking-[-0.02em]">
          Message and call the people you know.
        </p>
        <p className="m-0 mt-4 text-[15px] text-linen/80">
          On the web, Mac, Windows, Linux, iPhone and Android.
        </p>
      </div>
      <div
        aria-hidden="true"
        className="absolute -top-[90px] -right-[90px] h-[260px] w-[260px] rounded-full bg-white/[0.07]"
      />
    </section>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="m-0 mb-3 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm font-semibold text-scarlet"
    >
      {children}
    </p>
  );
}

const quietLink = cn("rounded text-ink-meta no-underline hover:text-ink", focusRing);

/** Your own server, the terms and the privacy policy, at the foot of the sign-in pages. */
function LegalLinks() {
  return (
    <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 text-[13px]">
      <UiLink href="/own-server" className={quietLink}>
        Use your own server
      </UiLink>
      <a href={`${SITE}/terms`} target="_blank" rel="noreferrer" className={quietLink}>
        Terms
      </a>
      <a href={`${SITE}/privacy`} target="_blank" rel="noreferrer" className={quietLink}>
        Privacy
      </a>
    </div>
  );
}

/**
 * The frame shared by every sign-in page. It fills the screen exactly: the brand panel and the form
 * side by side on wide screens, the logo row above the form on phones. Only a window shorter than
 * the form scrolls.
 */
export function AuthFrame({
  busy = false,
  footer = <LegalLinks />,
  children,
}: {
  busy?: boolean;
  /** What sits at the foot of the page. */
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex h-dvh bg-linen min-[900px]:gap-4 min-[900px]:p-4">
      <BrandPanel />
      <main
        aria-busy={busy}
        className="flex min-w-0 flex-1 flex-col overflow-y-auto px-6 pt-10 pb-[max(20px,env(safe-area-inset-bottom))] min-[900px]:px-10 min-[900px]:pt-8 min-[900px]:pb-4 [@media(max-height:700px)]:pt-6"
      >
        <div className="flex items-center gap-2.5 min-[900px]:hidden">
          <Logo size={40} variant="tile" />
          <span className="font-serif text-2xl font-medium text-blue">rhema</span>
        </div>
        <div className="mx-auto flex w-full max-w-[384px] flex-col pt-9 min-[900px]:flex-1 min-[900px]:justify-center min-[900px]:pt-0 [@media(max-height:700px)]:pt-5">
          {children}
        </div>
        {footer && <div className="mt-auto pt-6 [@media(max-height:700px)]:pt-4">{footer}</div>}
      </main>
    </div>
  );
}

export function AuthHeading({ children }: { children: ReactNode }) {
  return (
    <h1 className="m-0 font-serif text-[34px] leading-[1.05] font-medium tracking-[-0.02em] min-[900px]:text-[42px]">
      {children}
    </h1>
  );
}

export function AuthText({ children }: { children: ReactNode }) {
  return <p className="m-0 mt-2.5 mb-6 text-base leading-normal text-ink-secondary">{children}</p>;
}

const linkClass = cn("rounded font-semibold text-blue no-underline", focusRing);

/** A link back to the first sign-in page, at the top of the steps after it. */
export function BackLink({
  onClick,
  href,
  children,
}: {
  onClick?: () => void;
  href?: string;
  children: ReactNode;
}) {
  const className = cn(
    "mb-5 -ml-1 inline-flex items-center gap-1 self-start rounded px-1 text-sm font-semibold text-blue no-underline",
    focusRing,
  );
  const inner = (
    <>
      <ChevronLeftIcon size={16} strokeWidth={2.2} />
      {children}
    </>
  );
  return href ? (
    <UiLink href={href} className={className}>
      {inner}
    </UiLink>
  ) : (
    <button
      type="button"
      onClick={onClick}
      className={cn(className, "cursor-pointer border-0 bg-transparent")}
    >
      {inner}
    </button>
  );
}

/** "New to Rhema?" on sign-in and "Already have an account?" on sign-up. */
function SwitchLine({ mode }: { mode: AuthMode }) {
  const [params] = useSearchParams();
  const query = params.toString() ? `?${params.toString()}` : "";
  return (
    <p className="m-0 mt-7 text-center text-[15px] text-ink-secondary [@media(max-height:700px)]:mt-5">
      {mode === "signin" ? "New to Rhema? " : "Already have an account? "}
      <UiLink href={`${mode === "signin" ? "/sign-up" : "/sign-in"}${query}`} className={linkClass}>
        {mode === "signin" ? "Create an account" : "Sign in"}
      </UiLink>
    </p>
  );
}

export function SignInScreen({
  onProvider,
  onEmail,
  onPhone,
  onMfa,
  onBack,
  stage = "start",
  mode = "signin",
  error,
  busy = false,
}: SignInScreenProps) {
  const [params] = useSearchParams();
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const signUp = mode === "signup";

  const submit = (e: FormEvent) => {
    e.preventDefault();
    onEmail(email.trim(), password);
  };

  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    onMfa?.(code.trim());
  };

  if (stage === "verify") {
    return (
      <AuthFrame>
        <AuthHeading>Check your email</AuthHeading>
        <AuthText>
          Your email address is not verified yet. Open the link we sent to {email || "your inbox"}, then come
          back and sign in.
        </AuthText>
        <Button size="lg" onClick={onBack}>
          Back to sign in
        </Button>
      </AuthFrame>
    );
  }

  if (stage === "mfa") {
    return (
      <AuthFrame busy={busy}>
        <BackLink onClick={onBack}>Start again</BackLink>
        <AuthHeading>Enter your code</AuthHeading>
        <AuthText>Open your authenticator app and enter the 6-digit code it shows for Rhema.</AuthText>
        {error && <ErrorNote>{error}</ErrorNote>}
        <form onSubmit={submitCode} className="flex flex-col gap-3">
          <TextField
            label="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          />
          <Button type="submit" size="lg" disabled={busy || code.length !== 6}>
            {busy ? "Checking the code" : "Verify"}
          </Button>
        </form>
      </AuthFrame>
    );
  }

  if (emailOpen) {
    return (
      <AuthFrame busy={busy}>
        <BackLink onClick={() => setEmailOpen(false)}>Other ways to sign in</BackLink>
        <AuthHeading>Sign in with email</AuthHeading>
        <AuthText>Use the email address and password you chose for Rhema.</AuthText>
        {error && <ErrorNote>{error}</ErrorNote>}
        <form onSubmit={submit} aria-label="Sign in with email" className="flex flex-col gap-4">
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button type="submit" size="lg" className="mt-1" disabled={busy}>
            {busy ? "Signing in" : "Sign in"}
          </Button>
          <p className="m-0 text-center text-sm">
            <UiLink href="/forgot-password" className={linkClass}>
              Forgot your password?
            </UiLink>
          </p>
        </form>
      </AuthFrame>
    );
  }

  const query = params.toString() ? `?${params.toString()}` : "";
  const emailLabel = signUp ? "Sign up with email" : "Continue with email";
  let lead: string;
  if (onPhone) {
    lead = signUp
      ? "Your phone number is your Rhema account. We'll text you a code to confirm it."
      : "Enter your phone number and we'll text you a code.";
  } else {
    lead = signUp ? "Choose how you want to create your account." : "Choose how you want to sign in.";
  }

  return (
    <AuthFrame busy={busy}>
      <AuthHeading>{signUp ? "Create your account" : "Sign in to Rhema"}</AuthHeading>
      <AuthText>{lead}</AuthText>

      {error && <ErrorNote>{error}</ErrorNote>}

      {onPhone && (
        <>
          <PhoneStartForm
            onPhone={onPhone}
            busy={busy}
            label={signUp ? "Sign up with your phone number" : "Sign in with your phone number"}
          />
          <OrDivider />
        </>
      )}
      <ProviderButtons verb={signUp ? "Sign up with" : "Continue with"} busy={busy} onProvider={onProvider} />
      {signUp ? (
        <UiLink href={`/sign-up/email${query}`} className={cn(optionClass, "mt-2 no-underline")}>
          <MailIcon size={18} />
          {emailLabel}
        </UiLink>
      ) : (
        <button
          type="button"
          className={cn(optionClass, "mt-2")}
          disabled={busy}
          onClick={() => setEmailOpen(true)}
        >
          <MailIcon size={18} />
          {emailLabel}
        </button>
      )}

      <SwitchLine mode={mode} />
      {signUp && (
        <p className="m-0 mt-3 text-center text-[13px] leading-normal text-ink-meta">
          By creating an account you agree to the{" "}
          <a href={`${SITE}/terms`} target="_blank" rel="noreferrer" className="text-ink-secondary">
            Terms
          </a>{" "}
          and the{" "}
          <a href={`${SITE}/privacy`} target="_blank" rel="noreferrer" className="text-ink-secondary">
            Privacy Policy
          </a>
          .
        </p>
      )}
    </AuthFrame>
  );
}

const QUERY_ERRORS: Record<string, string> = {
  login_required: "You were signed out. Sign in again to continue.",
  failed: "That sign-in did not work. Try again, or use another way to sign in.",
  expired: "That sign-in took too long and has expired. Start again.",
};

const OFFLINE = "Rhema could not reach the server. Check your connection and try again.";

type PhoneFlow = {
  phone: string;
  startToken: string;
  resendAfter: number;
  sentAt: number;
  pinToken?: string;
};

type PageStage = SignInStage | "code" | "pin" | "name";

export function SignInPage({ mode = "signin" }: { mode?: AuthMode }) {
  const { api, signedIn, redirect, platform } = useSession();
  const phoneEnabled = usePhoneEnabled();
  const [params] = useSearchParams();
  const returnTo = returnPath(params);
  const [stage, setStage] = useState<PageStage>("start");
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [flow, setFlow] = useState<PhoneFlow | null>(null);
  const [newUser, setNewUser] = useState<User | null>(null);
  const [busy, setBusy] = useState(false);
  const queryError = params.get("error");
  const [error, setError] = useState<string | null>(
    queryError ? (QUERY_ERRORS[queryError] ?? QUERY_ERRORS.failed ?? null) : null,
  );

  // Desktop and mobile get a token back and keep it in the keychain. The web app gets a cookie.
  // A new phone account has no name yet, so it is asked for before the app opens.
  const finish = async (res: SignInResult) => {
    if (res.pin_required && res.pin_token) {
      const pinToken = res.pin_token;
      setFlow((f) => (f ? { ...f, pinToken } : f));
      setStage("pin");
      return;
    }
    if (res.mfa_required && res.mfa_token) {
      setMfaToken(res.mfa_token);
      setStage("mfa");
      return;
    }
    if (res.token) await platform.saveToken(res.token);
    const user = res.user ?? (res.token ? await api.me() : null);
    if (!user) setError(QUERY_ERRORS.failed ?? null);
    else if (res.new_account) {
      setNewUser(user);
      setStage("name");
    } else signedIn(user);
  };

  const attempt = async (job: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await job();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : OFFLINE);
    } finally {
      setBusy(false);
    }
  };

  const onPhone = (phone: string) =>
    attempt(async () => {
      const res = await api.phoneStart(phone, platform.authClient);
      setFlow({
        phone,
        startToken: res.start_token,
        resendAfter: res.resend_after ?? 30,
        sentAt: Date.now(),
      });
      setStage("code");
    });

  const onCode = (code: string) =>
    attempt(async () => {
      if (!flow) return;
      await finish(await api.phoneVerify(flow.startToken, code, platform.authClient));
    });

  const onPin = (pin: string) =>
    attempt(async () => {
      if (!flow?.pinToken) return;
      await finish(await api.phonePin(flow.pinToken, pin, platform.authClient));
    });

  const onForgotPin = async () => {
    if (!flow?.pinToken) return false;
    const pinToken = flow.pinToken;
    let sent = false;
    await attempt(async () => {
      await api.phonePinRecover(pinToken);
      sent = true;
    });
    return sent;
  };

  const onName = (name: string) =>
    attempt(async () => {
      if (!newUser) return;
      signedIn(await api.updateMe({ display_name: name }));
    });

  const onProvider = async (idp: SignInProvider) => {
    if (!platform.native) {
      redirect(`/auth/start?idp=${idp}&return_to=${encodeURIComponent(returnTo)}`);
      return;
    }
    // The system browser signs in and comes back on a rhema:// link.
    setError(null);
    try {
      signedIn(await platform.signInWith(idp));
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : OFFLINE);
    }
  };

  const onEmail = async (email: string, password: string) => {
    setBusy(true);
    setError(null);
    try {
      await finish(await api.passwordLogin(email, password, platform.authClient));
    } catch (e) {
      if (!(e instanceof ApiError)) setError(OFFLINE);
      else if (e.status === 401) setError("The email or password is not right.");
      else if (e.status === 403 && e.detail === "verify_email") setStage("verify");
      else setError(e.detail);
    } finally {
      setBusy(false);
    }
  };

  const onMfa = async (code: string) => {
    if (!mfaToken) return;
    setBusy(true);
    setError(null);
    try {
      await finish(await api.mfa(mfaToken, code, platform.authClient));
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : OFFLINE);
    } finally {
      setBusy(false);
    }
  };

  const restart = () => {
    setStage("start");
    setMfaToken(null);
    setFlow(null);
    setError(null);
  };

  if (stage === "code" && flow) {
    return (
      <PhoneCodeStep
        phone={flow.phone}
        resendAfter={flow.resendAfter}
        sentAt={flow.sentAt}
        busy={busy}
        error={error}
        onVerify={(code) => void onCode(code)}
        onResend={() => void onPhone(flow.phone)}
        onChangeNumber={restart}
      />
    );
  }
  if (stage === "pin") {
    return (
      <PinStep
        busy={busy}
        error={error}
        onPin={(pin) => void onPin(pin)}
        onForgot={onForgotPin}
        onBack={restart}
      />
    );
  }
  if (stage === "name") {
    return <NameStep busy={busy} error={error} onName={(name) => void onName(name)} />;
  }

  return (
    <SignInScreen
      stage={stage === "mfa" || stage === "verify" ? stage : "start"}
      mode={mode}
      error={error}
      busy={busy}
      {...(phoneEnabled ? { onPhone: (phone: string) => void onPhone(phone) } : {})}
      onProvider={(idp) => void onProvider(idp)}
      onEmail={(email, password) => void onEmail(email, password)}
      onMfa={(code) => void onMfa(code)}
      onBack={restart}
    />
  );
}
